import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Trash2, Ticket, Mail } from "lucide-react";

const AdminBrandAccess = () => {
    const [leads, setLeads] = useState([]);
    const [codes, setCodes] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [label, setLabel] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [l, c, e] = await Promise.all([
            api.get("/admin/brand-leads"), api.get("/admin/brand-codes"), api.get("/admin/enquiries"),
        ]);
        setLeads(l.data);
        setCodes(c.data);
        setEnquiries(e.data);
    };
    useEffect(() => { load(); }, []);

    const generate = async (lead, kind) => {
        setBusy(true);
        try {
            const r = await api.post("/admin/brand-codes", {
                ...(lead
                    ? { label: `${lead.contact_name} — ${lead.email}`, lead_id: lead.lead_id }
                    : { label: label.trim() }),
                ...(kind ? { kind } : {}),
            });
            navigator.clipboard?.writeText(r.data.code);
            toast.success(r.data.emailed_to
                ? `Code ${r.data.code} generated & emailed to ${r.data.emailed_to}`
                : `Code ${r.data.code} generated & copied — send it to the brand`);
            setLabel("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not generate code");
        } finally { setBusy(false); }
    };

    const revoke = async (code) => {
        if (!window.confirm(`Revoke ${code}? The brand won't be able to use it.`)) return;
        try {
            await api.delete(`/admin/brand-codes/${code}`);
            toast.success(`${code} revoked`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not revoke (already used?)");
        }
    };

    const copy = (code) => {
        navigator.clipboard.writeText(code);
        toast.success("Code copied");
    };

    return (
        <DashboardShell role="admin" title="Brand access" subtitle="Enquiries from brands + the unique codes that let them in">
            <div className="yc-card p-6">
                <div className="flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-yc-blue" />
                    <h3 className="font-heading text-xl font-bold">Brand enquiries ({leads.length})</h3>
                </div>
                <p className="text-sm text-yc-ink/70 mt-1">Brands request access via the join form. Generate their unique code and send it to them — they enter it after signing in with Google.</p>
                <div className="mt-4 space-y-3">
                    {leads.map((l) => (
                        <div key={l.lead_id} data-testid={`brand-lead-${l.lead_id}`} className="border-2 border-yc-ink rounded-2xl p-4 bg-white flex flex-wrap items-center gap-3">
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-heading font-bold">{l.contact_name}</div>
                                <div className="text-xs text-yc-ink/60">{l.email} · {l.phone}</div>
                                <div className="text-xs text-yc-ink/60">{l.location} · {l.num_locations} location(s) · {new Date(l.created_at).toLocaleDateString()}</div>
                            </div>
                            {l.status === "code_issued" ? (
                                <span className="yc-badge bg-yc-blue text-white text-[10px]">Code issued</span>
                            ) : (
                                <span className="yc-badge bg-yc-cream text-[10px]">New</span>
                            )}
                            <button data-testid={`lead-gen-code-${l.lead_id}`} disabled={busy} onClick={() => generate(l)} className="yc-btn-primary text-xs py-1.5 px-3 disabled:opacity-50">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Generate code
                            </button>
                        </div>
                    ))}
                    {leads.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-4">No brand enquiries yet.</div>}
                </div>
            </div>

            <div className="yc-card p-6">
                <h3 className="font-heading text-xl font-bold">Access codes ({codes.length})</h3>
                <p className="text-sm text-yc-ink/70 mt-1">A <strong>standard code</strong> lets a brand join. A <strong>partner code</strong> also lets them create campaigns free — they can still buy a plan later.</p>
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <input
                        data-testid="brand-code-label-input"
                        className="yc-input flex-1"
                        placeholder="Label (e.g. brand name) — for codes not tied to an enquiry"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
                    />
                    <button data-testid="brand-code-generate-btn" disabled={busy} onClick={() => generate()} className="yc-btn-primary disabled:opacity-50">
                        <Plus className="w-4 h-4 mr-2" /> Standard code
                    </button>
                    <button data-testid="partner-code-generate-btn" disabled={busy} onClick={() => generate(null, "sponsor")} className="yc-btn-secondary disabled:opacity-50">
                        <Plus className="w-4 h-4 mr-2" /> Partner code
                    </button>
                </div>
                <div className="mt-4 space-y-3">
                    {codes.map((c) => (
                        <div key={c.code} data-testid={`brand-code-row-${c.code}`} className={`border-2 border-yc-ink rounded-2xl p-4 flex flex-wrap items-center gap-3 ${c.used_by ? "opacity-60 bg-yc-cream" : "bg-white"}`}>
                            <div className="font-mono font-black tracking-widest text-lg">{c.code}</div>
                            {c.kind === "sponsor" && <span className="yc-badge bg-yc-pink text-white text-[10px]">Partner · free campaigns</span>}
                            <div className="flex-1 min-w-[140px]">
                                <div className="font-semibold text-sm">{c.label || "—"}</div>
                                <div className="text-xs text-yc-ink/60">
                                    {c.used_by ? `Used by ${c.used_email} · ${new Date(c.used_at).toLocaleDateString()}` : `Unused · created ${new Date(c.created_at).toLocaleDateString()}`}
                                </div>
                            </div>
                            {!c.used_by && (
                                <>
                                    <button data-testid={`brand-code-copy-${c.code}`} onClick={() => copy(c.code)} className="yc-btn-secondary text-xs py-2 px-3">
                                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                                    </button>
                                    <button data-testid={`brand-code-revoke-${c.code}`} onClick={() => revoke(c.code)} className="flex items-center gap-1.5 text-xs font-bold text-yc-pink border-2 border-yc-pink rounded-full px-3 py-2 hover:bg-yc-pink hover:text-white transition-colors">
                                        <Trash2 className="w-3.5 h-3.5" /> Revoke
                                    </button>
                                </>
                            )}
                        </div>
                    ))}
                    {codes.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-4">No codes generated yet.</div>}
                </div>
            </div>

            {/* Pricing-page enquiries (corporate / taster) */}
            <div className="yc-card p-6" data-testid="enquiries-card">
                <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-yc-pink" />
                    <h3 className="font-heading text-xl font-bold">Pricing enquiries ({enquiries.length})</h3>
                </div>
                <p className="text-sm text-yc-ink/70 mt-1">Corporate &ldquo;talk to us&rdquo; and taster enquiries from the pricing page.</p>
                <div className="mt-4 space-y-3">
                    {enquiries.map((e) => (
                        <div key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`} className="border-2 border-yc-ink rounded-2xl p-4 bg-white flex flex-wrap items-center gap-3">
                            <span className={`yc-badge text-[10px] ${e.kind === "corporate" ? "bg-yc-pink text-white" : "bg-yc-blue text-white"}`}>{e.kind}</span>
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-semibold text-sm">{e.name}{e.company ? ` · ${e.company}` : ""}</div>
                                <div className="text-xs text-yc-ink/60">{e.email}{e.message ? ` — ${e.message}` : ""}</div>
                            </div>
                            <span className="text-xs text-yc-ink/50">{e.created_at ? new Date(e.created_at).toLocaleDateString() : ""}</span>
                            <a href={`mailto:${e.email}`} className="yc-btn-secondary text-xs py-2 px-3">Reply</a>
                        </div>
                    ))}
                    {enquiries.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-4">No enquiries yet.</div>}
                </div>
            </div>
        </DashboardShell>
    );
};

export default AdminBrandAccess;
